import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import {
    clearAppointmentAuditData,
    clearNewAppointment,
    loadAppointmentAuditData,
    setOpenAppointmentSchedulerPanel,
    setParticipantsCurrentAppointment} from '../../../store/calendarService';
import { selectUsersForNamesById } from '../../../store/userSelectors';
import { loadUsersMinimalDetails } from '../../../store/userService';
import { reverseFormatDate } from '../../../utils/dateFunctions';
import { hasRole } from '../../../utils/userRoles';
import AuditAppointmentModal from '../../auditing/auditAppointments/AuditAppointmentModal';
import StaticField from '../../formElements/StaticField';
import Switch from '../../formElements/Switch';
import Panel from '../../ui/panel/Panel';
import MandatoryActivitiesSwitch from '../components/MandatoryActivitiesSwitch';
import StartAppointmentRow from '../components/StartAppointmentRow';
import StaticParticipant from '../components/StaticParticipant';
import { auditRoles, participantAccessRoles, viewAdviserRoles } from '../utils/calendarUtils';

import sidePanel from '../styles/schedulePanel.module.css';

const ParticipantAppointmentView = ({ currentEvent, currentLocation, currentParticipantEntry }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = { id: '', service: '' };
    const [newEntry, setNewEntry] = useState(initialState);
    const [isAuditable, setIsAuditable] = useState(false);
    const [showAuditModal, setShowAuditModal] = useState(false);
    const [auditLabel, setAuditLabel] = useState('');
    const [modalHeadingLabel, setModalHeadingLabel] = useState('');
    const [isAuditDisabled, setIsAuditDisabled] = useState(true);
    const [isAdviserHidden, setIsAdviserHidden] = useState(true);
    const [hasParticipantAccess, setHasParticipantAccess] = useState(false);

    // STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const { roles } = loggedInUser;
    const users = useSelector(selectUsersForNamesById);
    const serviceDetailsById = useSelector(
        (state) => state.entities.directusService.serviceDetailsById
    );
    const openAppointmentSchedulerPanel = useSelector(
        (state) => state.entities.calendarService.openAppointmentSchedulerPanel
    );

    // USEEFFECTS
    useEffect(() => {
        if (!currentParticipantEntry?.id) return;
        const { serviceId, contractId } = currentParticipantEntry;
        if (!serviceId || !contractId) return;
        if (!(currentEvent.userId in users))
            dispatch(loadUsersMinimalDetails([currentEvent.userId]));
    }, []);

    useEffect(() => {
        if (!roles?.length) return;
        setIsAuditDisabled(!hasRole(auditRoles, roles));
        setIsAdviserHidden(
            !hasRole(viewAdviserRoles, roles) && currentEvent.userId !== loggedInUser.id
        );
        setHasParticipantAccess(hasRole(participantAccessRoles, roles));
    }, [roles]);

    useEffect(() => {
        if (!(currentEvent.serviceId in serviceDetailsById)) return;
        setNewEntry({
            id: currentEvent.id,
            service: serviceDetailsById[currentEvent.serviceId]?.name || ''
        });
    }, [serviceDetailsById]);

    // EVENT HANDLERS
    const onPanelClose = () => {
        setNewEntry(initialState);
        dispatch(setParticipantsCurrentAppointment({}));
        dispatch(setOpenAppointmentSchedulerPanel(false));
        dispatch(clearNewAppointment());
    };

    const onAuditModalClose = (e, reason) => {
        e.stopPropagation();
        if (reason === 'backdropClick') return;
        setShowAuditModal(false);
        setAuditLabel('');
        setModalHeadingLabel('');
        dispatch(clearAppointmentAuditData());
    };

    const onSearchAuditItems = (label, modalHeadingLabel) => {
        dispatch(loadAppointmentAuditData('appointment', newEntry.id, label));
        setShowAuditModal(true);
        setAuditLabel(label);
        setModalHeadingLabel(modalHeadingLabel ?? label);
    };

    // RENDER
    return (
        <>
            <Panel width="500px" open={openAppointmentSchedulerPanel} onToggle={onPanelClose}>
                <div className={sidePanel.panelActions}>
                    <Switch
                        id={'auditDisplayFields'}
                        label="Show Audit Fields"
                        disabled={isAuditDisabled}
                        onOff={true}
                        customClass="auditSwitch"
                        checked={isAuditable}
                        onChange={(e) => setIsAuditable(e.target.checked)}
                    />
                </div>
                <div className={sidePanel.formWrapper}>
                    <StaticField content={newEntry.service} customClass="underlinedContent" />
                    {!isAdviserHidden && (
                        <StaticField
                            label="Adviser"
                            content={users[currentEvent.userId]?.loginEmailAddress || ''}
                        />
                    )}
                    <StaticField label="Appointment Type" content={currentEvent.type} />
                    <StaticField
                        label="Location"
                        content={currentLocation}
                        isAuditable={isAuditable}
                        onSearchAuditItems={onSearchAuditItems}
                    />
                    {currentEvent?.remoteCallUrl && (
                        <StaticField
                            id="remoteCallUrl"
                            label="Remote Call URL"
                            content={currentEvent.remoteCallUrl}
                        />
                    )}
                    {currentEvent?.additionalDetails && (
                        <StaticField
                            id="additionalDetails"
                            label="Additional Details"
                            content={currentEvent.additionalDetails}
                        />
                    )}
                    <StaticField
                        label="Start Time"
                        content={currentEvent.startTime}
                        isAuditable={isAuditable}
                        onSearchAuditItems={() => onSearchAuditItems('Start Date', 'Start Time')}
                    />
                    <StaticField
                        label="End Time"
                        content={currentEvent.endTime}
                        isAuditable={isAuditable}
                        onSearchAuditItems={() => onSearchAuditItems('End Date', 'End Time')}
                    />
                    <StaticField
                        label="Date"
                        content={reverseFormatDate(currentEvent.date)}
                        isAuditable={isAuditable}
                        onSearchAuditItems={() => onSearchAuditItems('Start Date', 'Date')}
                    />

                    <StaticParticipant
                        participant={currentParticipantEntry}
                        hasParticipantAccess={hasParticipantAccess}
                        onNav={onPanelClose}
                    />
                    <MandatoryActivitiesSwitch
                        isMandatory={currentEvent.mandatory}
                        disabled={true}
                    />

                    <StartAppointmentRow
                        disabled={true}
                        isUpdateDisabled={true}
                        hasParticipantAccess={hasParticipantAccess}
                        onPanelClose={onPanelClose}
                        appointment={currentEvent}
                        participant={currentParticipantEntry}
                        isAuditable={isAuditable}
                        onSearchAuditItems={onSearchAuditItems}
                    />
                </div>
            </Panel>
            {showAuditModal && (
                <div id="audit">
                    <AuditAppointmentModal
                        isOpen={showAuditModal}
                        label={auditLabel}
                        modalHeadingLabel={modalHeadingLabel}
                        onClose={onAuditModalClose}
                    />
                </div>
            )}
        </>
    );
};

ParticipantAppointmentView.propTypes = {
    currentEvent: PropTypes.object,
    currentLocation: PropTypes.string,
    currentParticipantEntry: PropTypes.object
};

export default ParticipantAppointmentView;
